import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));

  useEffect(() => {
    const storedToken = localStorage.getItem('admin_token');
    if (!storedToken) {
      setLoading(false);
      return;
    }
    const headers = { Authorization: `Bearer ${storedToken}` };
    axios.get(`${API}/auth/me`, { headers })
      .then(async () => {
        setIsAuthenticated(true);
        setToken(storedToken);
        // Sliding session: silently swap for a fresh 30-day token so an
        // active admin never hits expiry mid-work.
        try {
          const r = await axios.post(`${API}/auth/refresh`, {}, { headers });
          if (r.data?.access_token) {
            localStorage.setItem('admin_token', r.data.access_token);
            setToken(r.data.access_token);
          }
        } catch {
          // Non-fatal — the existing token is still valid.
        }
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          // Token genuinely invalid/expired — log out.
          localStorage.removeItem('admin_token');
          setIsAuthenticated(false);
          setToken(null);
        } else {
          // Transient failure (network blip, 502, timeout): keep the session.
          // Kicking the admin out here was the cause of "I keep getting
          // logged out while writing blogs".
          setIsAuthenticated(true);
          setToken(storedToken);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { access_token } = response.data;
      localStorage.setItem('admin_token', access_token);
      setToken(access_token);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setIsAuthenticated(false);
  }, []);

  const value = useMemo(() => ({
    isAuthenticated,
    loading,
    token,
    login,
    logout
  }), [isAuthenticated, loading, token, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
